import {
  LanguageModelV4,
  LanguageModelV4CallOptions,
  SharedV4Warning,
  LanguageModelV4Content,
  LanguageModelV4FinishReason,
  LanguageModelV4ProviderTool,
  LanguageModelV4StreamPart,
  LanguageModelV4ToolCall,
  LoadSettingError,
  LanguageModelV4GenerateResult,
  LanguageModelV4StreamResult,
} from "@ai-sdk/provider";
import { convertToWebLLMMessages } from "../utils/convert-to-webllm-messages";

import {
  AppConfig,
  ChatCompletionRequestNonStreaming,
  ChatCompletionRequestStreaming,
  CreateWebWorkerMLCEngine,
  InitProgressReport,
  MLCEngine,
  MLCEngineConfig,
  MLCEngineInterface,
} from "@mlc-ai/web-llm";
import { Availability } from "../types";
import {
  buildJsonToolSystemPrompt,
  parseJsonFunctionCalls,
  createUnsupportedSettingWarning,
  createUnsupportedToolWarning,
  isAutoToolChoice,
  isFunctionTool,
  processToolCallStream,
  type ToolDefinition,
  type DownloadProgressCallback,
} from "@browser-ai/shared";
import {
  prependSystemPromptToMessages,
  extractSystemPrompt,
} from "../utils/prompt-utils";
import {
  isMobile,
  checkWebGPU,
  doesBrowserSupportWebLLM,
} from "../utils/browser";

export { doesBrowserSupportWebLLM };

export type WebLLMModelId = string;

export interface WebLLMSettings {
  /**
   * Custom app configuration for WebLLM
   */
  appConfig?: AppConfig;
  /**
   * Progress callback for model initialization
   */
  initProgressCallback?: (progress: InitProgressReport) => void;
  /**
   * Engine configuration options
   */
  engineConfig?: MLCEngineConfig;
  /**
   * A web worker instance to run the model in.
   * When provided, the model will run in a separate thread.
   *
   * @default undefined
   */
  worker?: Worker;
}

type WebLLMConfig = {
  provider: string;
  modelId: WebLLMModelId;
  options: WebLLMSettings;
};

export class WebLLMLanguageModel implements LanguageModelV4 {
  readonly specificationVersion = "v4";
  readonly modelId: WebLLMModelId;
  readonly provider = "web-llm";

  private readonly config: WebLLMConfig;
  private engine?: MLCEngineInterface;
  private isInitialized = false;
  private initializationPromise?: Promise<void>;

  constructor(modelId: WebLLMModelId, options: WebLLMSettings = {}) {
    this.modelId = modelId;
    this.config = {
      provider: this.provider,
      modelId,
      options,
    };
  }

  readonly supportedUrls: Record<string, RegExp[]> = {
    // WebLLM doesn't support URLs natively
  };

  /**
   * Check if the model is initialized and ready to use
   * @returns true if the model is initialized, false otherwise
   */
  get isModelInitialized(): boolean {
    return this.isInitialized;
  }

  private async getEngine(
    options?: MLCEngineConfig,
    onInitProgress?: (progress: InitProgressReport) => void,
  ): Promise<MLCEngineInterface> {
    const availability = await this.availability();
    if (availability === "unavailable") {
      throw new LoadSettingError({
        message:
          "WebLLM is not available. This library requires a browser with WebGPU support.",
      });
    }

    if (this.engine && this.isInitialized) return this.engine;

    // If initialization is already in progress, wait for it
    if (this.initializationPromise) {
      await this.initializationPromise;
      if (this.engine) return this.engine;
    }

    this.initializationPromise = this._initializeEngine(
      options,
      onInitProgress,
    );
    await this.initializationPromise;

    if (!this.engine) {
      throw new LoadSettingError({
        message: "Engine initialization failed",
      });
    }

    return this.engine;
  }

  private async _initializeEngine(
    options?: MLCEngineConfig,
    onInitProgress?: (progress: InitProgressReport) => void,
  ): Promise<void> {
    try {
      // Create engine instance
      const engineConfig = {
        ...this.config.options.engineConfig,
        ...options,
        initProgressCallback:
          onInitProgress || this.config.options.initProgressCallback,
      };

      if (this.config.options.worker) {
        this.engine = await CreateWebWorkerMLCEngine(
          this.config.options.worker,
          this.modelId,
          engineConfig,
        );
      } else {
        this.engine = new MLCEngine(engineConfig);
        // Load the model
        await this.engine.reload(this.modelId);
      }

      this.isInitialized = true;
    } catch (error) {
      // Reset state on error so we can retry
      this.engine = undefined;
      this.isInitialized = false;
      this.initializationPromise = undefined;

      throw new LoadSettingError({
        message: `Failed to initialize WebLLM engine: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    }
  }

  private getArgs({
    prompt,
    maxOutputTokens,
    temperature,
    topP,
    topK,
    frequencyPenalty,
    presencePenalty,
    stopSequences,
    responseFormat,
    seed,
    tools,
    toolChoice,
    providerOptions,
  }: Parameters<LanguageModelV4["doGenerate"]>[0]) {
    const warnings: SharedV4Warning[] = [];

    const functionTools: ToolDefinition[] = (tools ?? [])
      .filter(isFunctionTool)
      .map((tool) => ({
        name: tool.name,
        description: tool.description,
        parameters: tool.inputSchema,
      }));

    const unsupportedTools = (tools ?? []).filter(
      (tool): tool is LanguageModelV4ProviderTool => !isFunctionTool(tool),
    );

    for (const tool of unsupportedTools) {
      warnings.push(
        createUnsupportedToolWarning(
          tool,
          "Only function tools are supported by WebLLM",
        ),
      );
    }

    if (topK != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "topK",
          "topK is not supported by WebLLM",
        ),
      );
    }

    if (stopSequences != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "stopSequences",
          "Stop sequences may not be fully implemented",
        ),
      );
    }

    if (presencePenalty != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "presencePenalty",
          "Presence penalty is not fully implemented",
        ),
      );
    }

    if (frequencyPenalty != null) {
      warnings.push(
        createUnsupportedSettingWarning(
          "frequencyPenalty",
          "Frequency penalty is not fully implemented",
        ),
      );
    }

    // `{ type: "auto" }` is the AI SDK's implicit default, sent on every call
    // whether or not tools are in play — and auto is what this provider does
    // anyway, so warning about it would fire on every request.
    if (toolChoice != null && !isAutoToolChoice(toolChoice)) {
      warnings.push(
        createUnsupportedSettingWarning(
          "toolChoice",
          "toolChoice is not supported by WebLLM",
        ),
      );
    }

    // Convert messages to WebLLM format
    const messages = convertToWebLLMMessages(prompt);

    // Build request options
    const requestOptions: ChatCompletionRequestNonStreaming = {
      messages,
      temperature,
      max_tokens: maxOutputTokens,
      top_p: topP,
      seed,
    };

    const webLLMOptions = providerOptions?.[this.provider];
    const extraBody = webLLMOptions?.extra_body as
      | Record<string, unknown>
      | undefined;
    if (extraBody) {
      // https://webllm.mlc.ai/docs/user/api_reference.html#generationconfig
      requestOptions.extra_body = {
        enable_thinking: extraBody.enable_thinking as boolean | undefined,
        enable_latency_breakdown: extraBody.enable_latency_breakdown as
          | boolean
          | undefined,
      };
    }

    // Handle response format
    if (responseFormat?.type === "json") {
      requestOptions.response_format = {
        type: "json_object",
        ...(responseFormat.schema && {
          schema: JSON.stringify(responseFormat.schema),
        }),
      };
    }

    return {
      messages,
      warnings,
      requestOptions,
      functionTools,
    };
  }

  /**
   * Generates a complete text response using WebLLM
   * @param options
   * @returns Promise resolving to the generated content with finish reason, usage stats, and any warnings
   * @throws {LoadSettingError} When WebLLM is not available or model needs to be downloaded
   * @throws {UnsupportedFunctionalityError} When unsupported features like file input are used
   */
  public async doGenerate(
    options: LanguageModelV4CallOptions,
  ): Promise<LanguageModelV4GenerateResult> {
    const converted = this.getArgs(options);
    const { messages, warnings, requestOptions, functionTools } = converted;

    // Extract system prompt and build tool calling prompt
    const {
      systemPrompt: originalSystemPrompt,
      messages: messagesWithoutSystem,
    } = extractSystemPrompt(messages);

    const systemPrompt = buildJsonToolSystemPrompt(
      originalSystemPrompt,
      functionTools,
      {
        allowParallelToolCalls: false,
      },
    );

    // Prepend system prompt to messages
    const promptMessages = prependSystemPromptToMessages(
      messagesWithoutSystem,
      systemPrompt,
    );

    const engine = await this.getEngine();

    const abortHandler = async () => {
      await engine.interruptGenerate();
    };

    if (options.abortSignal) {
      options.abortSignal.addEventListener("abort", abortHandler);
    }

    try {
      const response = await engine.chat.completions.create({
        ...requestOptions,
        messages: promptMessages,
        stream: false,
        ...(options.abortSignal &&
          !this.config.options.worker && { signal: options.abortSignal }),
      });

      const choice = response.choices[0];
      if (!choice) {
        throw new Error("No response choice returned from WebLLM");
      }

      const rawResponse = choice.message.content || "";

      // Parse JSON tool calls from response
      const { toolCalls, textContent } = parseJsonFunctionCalls(rawResponse);

      if (toolCalls.length > 0) {
        const toolCallsToEmit = toolCalls.slice(0, 1);

        const parts: LanguageModelV4Content[] = [];

        if (textContent) {
          parts.push({
            type: "text",
            text: textContent,
          });
        }

        for (const call of toolCallsToEmit) {
          parts.push({
            type: "tool-call",
            toolCallId: call.toolCallId,
            toolName: call.toolName,
            input: JSON.stringify(call.args ?? {}),
          } satisfies LanguageModelV4ToolCall);
        }

        return {
          content: parts,
          finishReason: { unified: "tool-calls", raw: "tool-calls" },
          usage: {
            inputTokens: {
              total: response.usage?.prompt_tokens,
              noCache: undefined,
              cacheRead: undefined,
              cacheWrite: undefined,
            },
            outputTokens: {
              total: response.usage?.completion_tokens,
              text: undefined,
              reasoning: undefined,
            },
          },
          request: { body: { ...requestOptions, messages: promptMessages } },
          warnings,
        };
      }

      const content: LanguageModelV4Content[] = [
        {
          type: "text",
          text: textContent || rawResponse,
        },
      ];

      let finishReason: LanguageModelV4FinishReason = {
        unified: "stop",
        raw: choice.finish_reason,
      };
      if (choice.finish_reason === "abort") {
        finishReason = { unified: "other", raw: choice.finish_reason };
      }

      return {
        content,
        finishReason,
        usage: {
          inputTokens: {
            total: response.usage?.prompt_tokens,
            noCache: undefined,
            cacheRead: undefined,
            cacheWrite: undefined,
          },
          outputTokens: {
            total: response.usage?.completion_tokens,
            text: undefined,
            reasoning: undefined,
          },
          raw: {
            total: response.usage?.total_tokens,
          },
        },
        request: { body: { ...requestOptions, messages: promptMessages } },
        warnings,
      };
    } catch (error) {
      throw new Error(
        `WebLLM generation failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    } finally {
      if (options.abortSignal) {
        options.abortSignal.removeEventListener("abort", abortHandler);
      }
    }
  }

  /**
   * Check the availability of the WebLLM model.
   * Note: On mobile devices with a worker, WebGPU detection is skipped since it
   * can't be done reliably. The actual availability will be determined at init.
   * @returns Promise resolving to "unavailable", "available", or "downloadable"
   */
  public async availability(): Promise<Availability> {
    if (this.isInitialized) {
      return "available";
    }

    // Skip on mobile if using a worker, since detecting GPU is unreliable.
    // Let worker initialization handle it and return an error if unavailable.
    if (this.config.options.worker && isMobile()) {
      return "downloadable";
    }

    const supported = checkWebGPU();
    return supported ? "downloadable" : "unavailable";
  }

  /**
   * Creates an engine session with download progress monitoring.
   *
   * @example
   * ```typescript
   * const model = await model.createSessionWithProgress(
   *   (progress) => {
   *     console.log(`Download progress: ${Math.round(progress * 100)}%`);
   *   }
   * );
   * ```
   *
   * @param onDownloadProgress Optional callback receiving progress values from 0 to 1
   * @returns Promise resolving to the model instance
   * @throws {LoadSettingError} When WebLLM is not available or model is unavailable
   */
  public async createSessionWithProgress(
    onDownloadProgress?: DownloadProgressCallback,
  ): Promise<WebLLMLanguageModel> {
    const adaptedCallback = onDownloadProgress
      ? (report: InitProgressReport) => {
          onDownloadProgress(report.progress);
        }
      : undefined;
    await this.getEngine(undefined, adaptedCallback);
    return this;
  }

  /**
   * Generates a streaming text response using WebLLM
   * @param options
   * @returns Promise resolving to a readable stream of text chunks and request metadata
   * @throws {LoadSettingError} When WebLLM is not available or model needs to be downloaded
   * @throws {UnsupportedFunctionalityError} When unsupported features like file input are used
   */
  public async doStream(
    options: LanguageModelV4CallOptions,
  ): Promise<LanguageModelV4StreamResult> {
    const converted = this.getArgs(options);
    const { messages, warnings, requestOptions, functionTools } = converted;

    // Extract system prompt and build tool calling prompt
    const {
      systemPrompt: originalSystemPrompt,
      messages: messagesWithoutSystem,
    } = extractSystemPrompt(messages);

    const systemPrompt = buildJsonToolSystemPrompt(
      originalSystemPrompt,
      functionTools,
      {
        allowParallelToolCalls: false,
      },
    );

    // Prepend system prompt to messages
    const promptMessages = prependSystemPromptToMessages(
      messagesWithoutSystem,
      systemPrompt,
    );

    const engine = await this.getEngine();
    const useWorker = this.config.options.worker != null;

    const abortHandler = async () => {
      await engine.interruptGenerate();
    };

    if (options.abortSignal) {
      options.abortSignal.addEventListener("abort", abortHandler);
    }

    const textId = "text-0";

    const stream = new ReadableStream<LanguageModelV4StreamPart>({
      async start(controller) {
        controller.enqueue({
          type: "stream-start",
          warnings,
        });

        let textStarted = false;
        let finished = false;

        const ensureTextStart = () => {
          if (!textStarted) {
            controller.enqueue({
              type: "text-start",
              id: textId,
            });
            textStarted = true;
          }
        };

        const emitTextDelta = (delta: string) => {
          if (!delta) return;
          ensureTextStart();
          controller.enqueue({
            type: "text-delta",
            id: textId,
            delta,
          });
        };

        const emitTextEndIfNeeded = () => {
          if (!textStarted) return;
          controller.enqueue({
            type: "text-end",
            id: textId,
          });
          textStarted = false;
        };

        const finishStream = (
          finishReason: LanguageModelV4FinishReason,
          usage?: {
            prompt_tokens?: number;
            completion_tokens?: number;
            total_tokens?: number;
          },
        ) => {
          if (finished) return;
          finished = true;
          emitTextEndIfNeeded();
          controller.enqueue({
            type: "finish",
            finishReason,
            usage: {
              inputTokens: {
                total: usage?.prompt_tokens,
                noCache: undefined,
                cacheRead: undefined,
                cacheWrite: undefined,
              },
              outputTokens: {
                total: usage?.completion_tokens,
                text: undefined,
                reasoning: undefined,
              },
              raw: {
                total: usage?.total_tokens,
              },
            },
          });
          controller.close();
        };

        try {
          const streamingRequest: ChatCompletionRequestStreaming = {
            ...requestOptions,
            messages: promptMessages,
            stream: true,
            stream_options: { include_usage: true },
            ...(options.abortSignal &&
              !useWorker && { signal: options.abortSignal }),
          };

          const response =
            await engine.chat.completions.create(streamingRequest);

          let lastUsage:
            | {
                prompt_tokens?: number;
                completion_tokens?: number;
                total_tokens?: number;
              }
            | undefined;
          let isAbort = false;

          const chunks = (async function* () {
            for await (const chunk of response) {
              const choice = chunk.choices[0];
              if (!choice) continue;
              if (choice.delta.content) yield choice.delta.content;
              if (chunk.usage) lastUsage = chunk.usage;
              if (choice.finish_reason === "abort") isAbort = true;
            }
          })();

          const result = await processToolCallStream(
            chunks,
            emitTextDelta,
            controller,
          );

          if (result.trailingText) {
            emitTextDelta(result.trailingText);
          }

          const finishReason: LanguageModelV4FinishReason = isAbort
            ? { unified: "other", raw: "abort" }
            : result.toolCallDetected
              ? { unified: "tool-calls", raw: "tool-calls" }
              : { unified: "stop", raw: "stop" };

          finishStream(finishReason, lastUsage);
        } catch (error) {
          // Propagate all other errors.
          controller.error(error);
        } finally {
          if (options.abortSignal) {
            options.abortSignal.removeEventListener("abort", abortHandler);
          }
          if (!finished) {
            controller.close();
          }
        }
      },
    });

    return {
      stream,
      request: { body: { ...requestOptions, messages: promptMessages } },
    };
  }
}
